import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: string;
}

/**
 * Checkbox padrão do design system.
 *
 * Variante de input com check custom estilizado.
 * Acessível via input checkbox nativo (peer).
 *
 * <Checkbox label="Aceito os termos" />
 * <Checkbox label="Líder do departamento" hint="Pode editar workflows" />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, hint, className, disabled, ...props }, ref) => {
    return (
      <label
        className={cn(
          'inline-flex items-start gap-2.5 cursor-pointer select-none',
          disabled && 'opacity-60 cursor-not-allowed',
          className
        )}
      >
        <span className="relative flex-shrink-0 mt-0.5">
          <input
            ref={ref}
            type="checkbox"
            disabled={disabled}
            className="sr-only peer"
            {...props}
          />
          <span
            className={cn(
              'block w-4 h-4 rounded border-2 transition-colors',
              'border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface',
              'peer-checked:bg-red peer-checked:border-red',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-red-soft dark:peer-focus-visible:ring-red-soft-dark peer-focus-visible:ring-offset-1'
            )}
          />
          {/* Check icon */}
          <svg
            className="absolute top-0.5 left-0.5 w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        {(label || hint) && (
          <span className="flex flex-col">
            {label && <span className="text-sm text-ink-1 dark:text-ink-dark-1">{label}</span>}
            {hint && <span className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">{hint}</span>}
          </span>
        )}
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
